import { PhotoTrigger } from '../components/photo-viewer.tsx';
import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { quoteRequestApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { PlanRequiredPage, companyHasPlanFeature, usePlanFeature } from '../components/plan-gate.tsx';
import { Alert, Button, EmptyState, LoadingBlock, Pagination, Select } from '../components/ui.tsx';
import { WorkPage, WorkSection } from '../components/work-page.tsx';
import AddressLink from '../components/address-link.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { requestActionScope, useRequestActions, type RequestAction } from '../hooks/use-request-actions.ts';
import { QUOTE_REQUEST_SOURCES, QUOTE_REQUEST_STATUSES } from '@shared/constants/index.ts';
import { RiskBadge, SlaTimer, money } from '../components/photo-quotes/parts.tsx';
import { QuoteActionsOverflow } from '../components/quote/actions-overflow.tsx';
import { PhotoIntakeStatusBadge } from './photo-quotes.tsx';
import type { QuoteRequest } from '@shared/types/index.ts';
import './requests-page.css';
import { formatNumericInstant } from '../i18n/datetime.ts';

/**
 * The inbox behind the public quote-request form. Each lead is reviewed by a
 * person: convert creates the customer and a draft quote (and jumps straight
 * into the editor), dismiss closes it. Nothing a visitor typed becomes a real
 * record until one of those happens.
 */
/**
 * The plan decides whether this page exists for a company at all. The check
 * is a wrapper rather than an early return inside the page, so the body's
 * hooks keep running in the same order on every render.
 */
export default function RequestsPage() {
  const { t } = useTranslation();
  const { user, company, currentLocation, impersonatedBy } = useSession();
  const scope = requestActionScope({ user, company, location: currentLocation, impersonatedBy });
  if (!usePlanFeature('online_intake')) return <PlanRequiredPage feature="online_intake" title={t('requests.title')} />;
  return <RequestsPageContent key={scope} scope={scope} />;
}

function RequestsPageContent({ scope }: { scope: string }) {
  const { t } = useTranslation();
  useDocumentTitle(t('requests.documentTitle'));
  const { can, company } = useSession();
  const location = useLocation();

  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? (params.has('photoFilter') ? '' : 'new');
  const source = params.get('source') ?? '';
  const photoFilter = params.get('photoFilter') ?? '';
  function filter(next: { status?: string; source?: string; photoFilter?: string }) {
    const updated = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) updated.set(key, value);
    setParams(updated, { replace: true });
    setPage(1);
  }
  const [page, setPage] = useState(1);
  const actions = useRequestActions(scope, JSON.stringify([location.key, page]));
  // Photo quotes (spec WILD-05) arrive in this inbox too, behind their flag.
  const photoQuotes = company?.photoQuotesEnabled === true;

  const query = useQuery({
    queryKey: ['quote-requests', scope, { status, source, photoFilter, page }],
    refetchInterval: photoQuotes ? 15_000 : false,
    queryFn: () =>
      quoteRequestApi.list({
        status: status || undefined,
        source: source || undefined,
        photoFilter: photoFilter || undefined,
        page,
        pageSize: 25,
      }),
  });

  const STATUS_OPTIONS = [
    { value: '', label: t('requests.statusAll') },
    ...QUOTE_REQUEST_STATUSES.map((value) => ({ value, label: t(`requests.status.${value}`) })),
  ];

  return (
    <WorkPage
      title={t('requests.title')}
      description={t('requests.description')}
      actions={
        photoQuotes ? (
          <QuoteActionsOverflow label={t('requests.moreActions')}>
            <Link to="/quotes/photos/demo" className="btn btn-ghost">
              {t('photoQuotes.demo.tryLink')}
            </Link>
            {can('company:write') ? (
              <Link to="/settings#settings-photoQuotes" className="btn btn-ghost">
                {t('photoQuotes.queue.settingsLink')}
              </Link>
            ) : null}
          </QuoteActionsOverflow>
        ) : undefined
      }
    >
      {!company?.intakePath ? (
        <div className="mb-6">
          <Alert tone="info" title={t('requests.formOffTitle')}>
            <p>
              {t('requests.formOffBody')}{' '}
              <Link to="/settings" className="font-semibold underline">
                {t('requests.formOffLink')}
              </Link>
            </p>
          </Alert>
        </div>
      ) : null}

      <div className="card mb-6 grid max-w-xl gap-x-4 sm:grid-cols-2">
        <Select
          label={t('requests.statusLabel')}
          options={STATUS_OPTIONS}
          disabled={Boolean(photoFilter)}
          value={status}
          onChange={(event) => {
            filter({ status: event.target.value, photoFilter: '' });
          }}
        />
        {photoQuotes ? (
          <Select
            label={t('requests.sourceLabel')}
            options={[
              { value: '', label: t('requests.sourceAll') },
              ...QUOTE_REQUEST_SOURCES.map((value) => ({ value, label: t(`requests.source.${value}`) })),
            ]}
            value={source}
            onChange={(event) => {
              filter({ source: event.target.value, photoFilter: '' });
            }}
          />
        ) : null}
        {photoQuotes && source === 'photo_dump' ? (
          <Select
            label={t('requests.photoStatusLabel')}
            value={photoFilter}
            options={[
              { value: '', label: t('requests.statusAll') },
              ...['open', 'sent', 'closed'].map((value) => ({ value, label: t(`photoQuotes.queue.filter.${value}`) })),
            ]}
            onChange={(event) => filter({ photoFilter: event.target.value, status: '' })}
          />
        ) : null}
      </div>

      <WorkSection title={t('requests.statusAll')}>
        {query.isLoading ? <LoadingBlock label={t('requests.loading')} /> : null}
        {query.isError ? (
          <Alert tone="error" title={t('requests.loadErrorTitle')}>
            <p>{(query.error as Error).message}</p>
            <Button
              className="mt-2"
              busy={query.isFetching}
              onClick={() => void query.refetch({ cancelRefetch: false })}
            >
              {t('common.retry')}
            </Button>
          </Alert>
        ) : null}

        {query.isSuccess && query.data.items.length === 0 ? (
          <EmptyState title={t('requests.emptyTitle')}>
            <p>{status === 'new' ? t('requests.emptyNew') : t('requests.emptyFiltered')}</p>
          </EmptyState>
        ) : null}

        <div className="space-y-4">
          {query.data?.items.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              canHandle={can('quotes:write') && request.status === 'new'}
              canConvert={can('customers:write')}
              pending={actions.pending[request.id]}
              converting={actions.converting}
              error={actions.errors[request.id]}
              onConvert={() => void actions.run(request.id, 'convert')}
              onDismiss={() => void actions.run(request.id, 'dismiss')}
            />
          ))}
        </div>

        {/* The shared pager, which also walks an out-of-range page back: dismissing
          the last request on page 2 must not strand the operator on an empty page
          with no controls. */}
        <Pagination page={page} totalPages={query.data?.totalPages} onPageChange={setPage} />
      </WorkSection>
    </WorkPage>
  );
}

/**
 * The visitor's photos, loaded per card only when the row says any exist.
 * Served same-origin through the Worker, so plain <img> tags carry the
 * session like any other authenticated request.
 */
function RequestPhotos({ request }: { request: QuoteRequest }) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['quote-request-photos', request.id],
    queryFn: () => quoteRequestApi.photos(request.id),
  });
  const photos = query.data?.photos ?? [];
  return (
    <div className="mt-3">
      {query.isPending ? <LoadingBlock label={t('requests.photosLoading')} /> : null}
      {query.isError ? (
        <Alert tone="error" title={t('requests.photosLoadError')}>
          <Button className="mt-2" busy={query.isFetching} onClick={() => void query.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}
      {photos.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {photos.map((photo) => (
            <PhotoTrigger
              key={photo.id}
              src={quoteRequestApi.photoUrl(request.id, photo.id)}
              alt={t('requests.photoAlt', { name: request.name })}
              group={`request-${request.id}`}
            >
              <img
                src={quoteRequestApi.photoUrl(request.id, photo.id)}
                alt={t('requests.photoAlt', { name: request.name })}
                className="h-20 w-20 rounded border border-[color:var(--color-line)] object-cover"
                loading="lazy"
              />
            </PhotoTrigger>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RequestCard({
  request,
  canHandle,
  canConvert,
  pending,
  converting,
  error,
  onConvert,
  onDismiss,
}: {
  request: QuoteRequest;
  canHandle: boolean;
  canConvert: boolean;
  pending: RequestAction | undefined;
  converting: boolean;
  error: string | undefined;
  onConvert: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const { company } = useSession();
  const reviewPhoto =
    request.photoIntake && company?.photoQuotesEnabled === true && companyHasPlanFeature(company, 'magic_quote');

  const facts = [
    request.propertyType ? t(`requests.propertyType.${request.propertyType}`) : null,
    request.bedrooms !== null ? t('requests.bedrooms', { count: request.bedrooms }) : null,
    request.bathrooms !== null ? t('requests.bathrooms', { count: request.bathrooms }) : null,
    request.squareFeet !== null ? t('requests.squareFeet', { count: request.squareFeet }) : null,
    request.frequency ? t(`requests.frequency.${request.frequency}`) : null,
  ].filter(Boolean);

  return (
    <article className="card request-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            {request.name}
            <span className="badge ml-2 align-middle text-xs">{t(`requests.source.${request.source ?? 'form'}`)}</span>
          </h2>
          <p className="text-sm text-[color:var(--color-muted)]">
            {[request.email, request.phone].filter(Boolean).join(' · ')}
          </p>
          {request.address ? (
            <p className="mt-1 text-sm">
              <AddressLink address={request.address} />
            </p>
          ) : null}
        </div>
        <div className="text-right text-sm text-[color:var(--color-muted)]">
          <p>{formatNumericInstant(request.createdAt)}</p>
          {reviewPhoto && request.photoIntake ? (
            <PhotoIntakeStatusBadge status={request.photoIntake.status} />
          ) : (
            <p>{t(`requests.status.${request.status}`)}</p>
          )}
        </div>
      </div>

      {facts.length > 0 ? <p className="mt-2 text-sm">{facts.join(' · ')}</p> : null}
      {request.message ? (
        <blockquote className="mt-2 border-l-4 border-[color:var(--color-line)] pl-3 text-sm text-[color:var(--color-muted)]">
          {request.message}
        </blockquote>
      ) : null}

      {request.ai ? (
        <div className="mt-3 rounded border border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] p-3 text-sm">
          <p className="mb-1 font-semibold">
            {t('requests.aiReadyTitle', { count: request.ai.roomCount })}
            <span className="ml-2 font-normal text-[color:var(--color-muted)]">
              {t(`requests.aiConfidence.${request.ai.confidence}`)}
            </span>
          </p>
          <p>{request.ai.summary}</p>
          {request.ai.photoNotes ? <p className="mt-1">{request.ai.photoNotes}</p> : null}
          {request.status === 'new' ? (
            <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('requests.aiReadyHint')}</p>
          ) : null}
        </div>
      ) : request.aiStatus === 'pending' ? (
        <p className="mt-3 text-xs text-[color:var(--color-muted)]">{t('requests.aiPending')}</p>
      ) : null}

      {request.photoCount > 0 ? <RequestPhotos request={request} /> : null}

      {request.source === 'photo_dump' ? <PhotoQuoteLine request={request} /> : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {reviewPhoto ? (
          <Link to={`/requests/photo/${request.photoIntake!.id}`} className="btn btn-primary">
            {t('requests.openPhotoQuote')}
          </Link>
        ) : canHandle && canConvert ? (
          <Button
            variant="primary"
            busy={pending === 'convert'}
            disabled={Boolean(pending) || converting}
            onClick={onConvert}
          >
            {t('requests.convert')}
          </Button>
        ) : request.convertedQuoteId ? (
          <Link to={`/quotes/${request.convertedQuoteId}`} className="btn btn-primary">
            {t('requests.openConvertedQuote')}
          </Link>
        ) : null}
        {canHandle || (reviewPhoto && request.convertedQuoteId) ? (
          <QuoteActionsOverflow label={t('requests.moreActions')}>
            {reviewPhoto && canHandle && canConvert ? (
              <Button busy={pending === 'convert'} disabled={Boolean(pending) || converting} onClick={onConvert}>
                {t('requests.convert')}
              </Button>
            ) : null}
            {canHandle ? (
              <Button busy={pending === 'dismiss'} disabled={Boolean(pending)} onClick={onDismiss}>
                {t('requests.dismiss')}
              </Button>
            ) : null}
            {reviewPhoto && request.convertedQuoteId ? (
              <Link to={`/quotes/${request.convertedQuoteId}`} className="btn btn-ghost">
                {t('requests.openConvertedQuote')}
              </Link>
            ) : null}
          </QuoteActionsOverflow>
        ) : null}
      </div>
      {error ? (
        <div className="mt-3">
          <Alert tone="error" title={t('requests.actionErrorTitle')}>
            {error}
          </Alert>
        </div>
      ) : null}
    </article>
  );
}

/**
 * What a photo-quote lead adds to its card. On Pro, the read's price and risk
 * and the way into the review page; on Starter, which collects the photos
 * without reading them, the one line that says what Pro would do with them
 * (spec WILD-05, upgrade placement 2) — to the owner, who can act on it.
 * Nothing while the deployment's flag has photo quotes off for this company:
 * the review page would not open, and there is nothing to upgrade to. The
 * request itself is still converted or dismissed like any other.
 */
function PhotoQuoteLine({ request }: { request: QuoteRequest }) {
  const { t } = useTranslation();
  const { company, user } = useSession();
  const intake = request.photoIntake;
  if (!intake || company?.photoQuotesEnabled !== true) return null;

  if (!companyHasPlanFeature(company, 'magic_quote')) {
    if (user?.role !== 'owner' || request.status !== 'new') return null;
    return (
      <p className="mt-3 rounded border border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] p-3 text-sm">
        {t('requests.photoQuoteNudge', { count: request.photoCount })}{' '}
        <Link to="/settings#settings-billing" className="font-semibold underline">
          {t('requests.photoQuoteNudgeCta')}
        </Link>
      </p>
    );
  }

  return (
    <p className="mt-3 flex flex-wrap items-center gap-2 text-sm">
      {intake.priceCents !== null && intake.priceCents > 0 ? (
        <span className="font-semibold">{money(intake.priceCents, company?.currencyCode ?? 'USD')}</span>
      ) : null}
      <RiskBadge level={intake.riskLevel} />
      <SlaTimer dueAt={intake.slaDueAt} status={intake.status} />
    </p>
  );
}
